import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { BaseModalComponent } from '../../../rnpl-common/components';
import { ToasterService } from '../../../ui-components/toaster';
import {
  SalesOrderConflictPositionModel,
  SalesOrderIssues,
  SalesOrderModel,
  SalesOrderPositionModel
} from 'projects/workspace/src/app/sales-order/models/sales-order.model';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { ProductModel, ProductsService } from '../../../products';
import { ProductTypes } from '../../../products/product-types';
import { CustomSearchFn, getProductSearchValue } from '../../../rnpl-common/helpers';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';

@Component({
  selector: 'rnpl-resolve-conflict-modal',
  templateUrl: './resolve-conflict-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResolveConflictModalComponent extends BaseModalComponent implements OnInit {
  public companyProfile: CompanyProfile;
  public issues: SalesOrderIssues;
  public productTypes = ProductTypes;
  public hasInvalidPosition = false;

  public productsList: ProductModel[] = [];
  public issuedPositions: SalesOrderConflictPositionModel[] = [];

  public customSearchFn = CustomSearchFn;

  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(
    public toasterService: ToasterService,
    public translateService: TranslateService,
    public productsService: ProductsService,
    public salesOrderApi: SalesOrderApiService,
    public readonly dialogRef: MatDialogRef<ResolveConflictModalComponent>,
    private readonly cdr: ChangeDetectorRef,
    private readonly store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: { salesOrder: SalesOrderModel }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this._destroy))
      .subscribe((profile: CompanyProfile) => this.companyProfile = profile);
  }

  ngOnInit() {
    this.issues = cloneDeep(this.salesOrder.issues);
    this.issuedPositions = cloneDeep(
      this.salesOrder.issues.positions.filter((p: SalesOrderConflictPositionModel) => !p.productId)
    );

    this.getProducts();
  }

  private getProducts(): void {
    this.productsService.getActiveProducts()
      .pipe(takeUntil(this._destroy))
      .subscribe((products: ProductModel[]) => {
        this.productsList = this.prepareProductsList(products);
        if (this.issuedPositions.length) {
          this.issuedPositions = this.issuedPositions.map((p: SalesOrderConflictPositionModel) => ({
            ...p,
            productsList: [
              ...products
                .filter((product: ProductModel) => product.type === p.productType)
                .filter((product: ProductModel) => product.unit_type === p.unitType)
            ] // add own product list to each conflicted position
          }));
          this.cdr.detectChanges();
        }
      });
  }

  public prepareProductsList(products): ProductModel[] {
    return products.map(item => ({
      ...item,
      searchLabel: getProductSearchValue(item, this.translateService)
    }));
  }

  public selectProduct(product: ProductModel, position: SalesOrderConflictPositionModel): void {
    // update position for view template
    position.productId = +product.id;
    position.productName = product.name;
    position.productType = product.type as ProductTypes;

    this.updIssuesPositionProductId(position.positionId, +product.id);
  }


  public updIssuesPositionProductId(positionId: number, value: number): void {
    // update position sent data
    const index = this.issues.positions.findIndex((p: SalesOrderPositionModel) => p.positionId === positionId);
    this.issues.positions[index].productId = value;
  }

  public submitResolve(): void {
    if (this.issues.positions.some((p: SalesOrderPositionModel) => !p.productId)) {
      this.hasInvalidPosition = true;
      return;
    }

    if (this.submitRequest$.getValue()) { return; }
    this.submitRequest$.next(true);

    const issues = {
      positions: this.issues.positions
    };

    this.salesOrderApi.resolveSalesOrderIssue(this.salesOrder.id, issues)
      .pipe(takeUntil(this._destroy))
      .subscribe((salesOrder: SalesOrderModel) => {
        this.displayMessage('success', 'SO.ISSUES_RESOLVED');
        this.dialogRef.close(salesOrder);
      }, error => {
        this.submitRequest$.next(false);
        this.displayMessage('error', error.error.message);
      });
  }

  get salesOrder(): SalesOrderModel { return this.data.salesOrder; }

  onClick(result: boolean): void {
    this.dialogRef.close(result);
  }
}
